var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { EventEmitter } from "eventemitter3";
import { VERSION_METAFRAME, METAPAGE_KEY_STATE, METAPAGE_KEY_DEFINITION, } from "./Constants";
import { ApiPayloadPluginRequestMethod, JsonRpcMethodsFromParent, JsonRpcMethodsFromChild, } from "./v0_4";
import { isDebugFromUrlsParams, stringToRgb, log as MetapageToolsLog, merge, pageLoaded, } from "./MetapageTools";
import { isIframe } from "./Shared";
var MetaframeLoadingState;
(function (MetaframeLoadingState) {
    MetaframeLoadingState["WaitingForPageLoad"] = "WaitingForPageLoad";
    MetaframeLoadingState["SentSetupIframeClientRequest"] = "SentSetupIframeClientRequest";
    MetaframeLoadingState["Ready"] = "Ready";
})(MetaframeLoadingState || (MetaframeLoadingState = {}));
var MetaframeEvents;
(function (MetaframeEvents) {
    MetaframeEvents["Connected"] = "connected";
    MetaframeEvents["Error"] = "error";
    MetaframeEvents["Input"] = "input";
    MetaframeEvents["Inputs"] = "inputs";
    MetaframeEvents["Message"] = "message";
})(MetaframeEvents || (MetaframeEvents = {}));
export class Metaframe extends EventEmitter {
    constructor(options) {
        super();
        this._inputPipeValues = {};
        this._outputPipeValues = {};
        this._state = MetaframeLoadingState.WaitingForPageLoad;
        this._messageSendCount = 0;
        this.debug = isDebugFromUrlsParams();
        this.id = window.name;
        this.debug = isDebugFromUrlsParams();
        this._isIframe = isIframe();
        this.addListener = this.addListener.bind(this);
        this.dispose = this.dispose.bind(this);
        this.error = this.error.bind(this);
        this.getInput = this.getInput.bind(this);
        this.getInputs = this.getInputs.bind(this);
        this.getOutput = this.getOutput.bind(this);
        this.getOutputs = this.getOutputs.bind(this);
        this.log = this.log.bind(this);
        this.logInternal = this.logInternal.bind(this);
        this.onInput = this.onInput.bind(this);
        this.onInputs = this.onInputs.bind(this);
        this.onMessage = this.onMessage.bind(this);
        this.sendRpc = this.sendRpc.bind(this);
        this.setInput = this.setInput.bind(this);
        this.setInputs = this.setInputs.bind(this);
        this.setInternalInputsAndNotify =
            this.setInternalInputsAndNotify.bind(this);
        this.setOutput = this.setOutput.bind(this);
        this.setOutputs = this.setOutputs.bind(this);
        this.warn = this.warn.bind(this);
        this._resolveSetupIframeServerResponse =
            this._resolveSetupIframeServerResponse.bind(this);
        this.addListenerReturnDisposer = this.addListenerReturnDisposer.bind(this);
        this.connected = this.connected.bind(this);
        this.disableNotifyOnHashUrlChange =
            this.disableNotifyOnHashUrlChange.bind(this);
        this._onHashUrlChange = this._onHashUrlChange.bind(this);
        if (!this._isIframe) {
            this.log("Not an iframe, metaframe code disabled");
            return;
        }
        const thisRef = this;
        pageLoaded().then(() => {
            this.log("pageLoaded");
            window.addEventListener("message", this.onMessage);
            thisRef.sendRpc(JsonRpcMethodsFromChild.SetupIframeClientRequest, {
                version: Metaframe.version,
            });
            thisRef._state = MetaframeLoadingState.SentSetupIframeClientRequest;
        });
        if (!(options === null || options === void 0 ? void 0 : options.disableHashChangeEvent)) {
            window.addEventListener("hashchange", this._onHashUrlChange);
        }
    }
    _resolveSetupIframeServerResponse(params) {
        if (this._state === MetaframeLoadingState.WaitingForPageLoad) {
            throw "Got message but page has not finished loading, we should never get in this state";
        }
        if (!this._parentId) {
            this._parentVersion = params.version;
            this.color = stringToRgb(this.id);
            this._parentId = params.parentId;
            this.log(`metapage[${this._parentId}](v${this._parentVersion ? this._parentVersion : "unknown"}) registered`);
            this._inputPipeValues =
                params.state && params.state.inputs
                    ? params.state.inputs
                    : this._inputPipeValues;
            this._state = MetaframeLoadingState.Ready;
            this.sendRpc(JsonRpcMethodsFromChild.SetupIframeServerResponseAck, {
                version: Metaframe.version,
            });
            if (this._inputPipeValues &&
                Object.keys(this._inputPipeValues).length > 0) {
                this.emit(MetaframeEvents.Inputs, this._inputPipeValues);
                Object.keys(this._inputPipeValues).forEach((pipeId) => this.emit(MetaframeEvents.Input, pipeId, this._inputPipeValues[pipeId]));
            }
            this.emit(MetaframeEvents.Inputs, this._inputPipeValues);
            if (params.plugin) {
                this.plugin = new MetaframePlugin(this);
            }
            this.emit(MetaframeEvents.Connected);
        }
        else {
            this.log("Got JsonRpcMethods.SetupIframeServerResponse but already resolved");
        }
    }
    connected() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._state === MetaframeLoadingState.Ready) {
                return;
            }
            return new Promise((resolve, _) => {
                let disposer;
                disposer = this.addListenerReturnDisposer(MetaframeEvents.Connected, () => {
                    resolve();
                    disposer();
                });
            });
        });
    }
    addListenerReturnDisposer(event, listener) {
        super.addListener(event, listener);
        const disposer = () => {
            super.removeListener(event, listener);
        };
        return disposer;
    }
    log(o, color, backgroundColor) {
        if (!this.debug) {
            return;
        }
        this.logInternal(o, color ? color : this.color);
    }
    warn(o) {
        if (!this.debug) {
            return;
        }
        this.logInternal(o, "000", this.color);
    }
    error(err) {
        this.logInternal(err, this.color, "f00");
    }
    logInternal(o, color, backgroundColor) {
        let s;
        if (typeof o === "string") {
            s = o;
        }
        else if (typeof o === "number") {
            s = o + "";
        }
        else {
            s = JSON.stringify(o);
        }
        color = color ? color + "" : color;
        s = (this.id ? `Metaframe[${this.id}] ` : "") + `${s}`;
        MetapageToolsLog(s, color, backgroundColor);
    }
    dispose() {
        super.removeAllListeners();
        window.removeEventListener("message", this.onMessage);
        this.disableNotifyOnHashUrlChange();
        this._inputPipeValues = undefined;
        this._outputPipeValues = undefined;
    }
    addListener(event, listener) {
        super.addListener(event, listener);
        if (event === MetaframeEvents.Inputs) {
            window.setTimeout(() => {
                if (this._inputPipeValues) {
                    listener(this._inputPipeValues);
                }
            }, 0);
        }
        return this;
    }
    onInput(pipeId, listener) {
        return this.addListenerReturnDisposer(MetaframeEvents.Input, (pipe, value) => {
            if (pipeId === pipe) {
                listener(value);
            }
        });
    }
    onInputs(listener) {
        const disposer = this.addListenerReturnDisposer(MetaframeEvents.Inputs, listener);
        return disposer;
    }
    setInput(pipeId, blob) {
        var inputs = {};
        inputs[pipeId] = blob;
        this.setInputs(inputs);
    }
    setInputs(inputs) {
        this.sendRpc(JsonRpcMethodsFromChild.InputsUpdate, inputs);
    }
    setInternalInputsAndNotify(inputs) {
        if (!merge(this._inputPipeValues, inputs)) {
            return;
        }
        Object.keys(inputs).forEach((pipeId) => this.emit(MetaframeEvents.Input, pipeId, inputs[pipeId]));
        this.emit(MetaframeEvents.Inputs, inputs);
    }
    getInput(pipeId) {
        console.assert(!!pipeId);
        return this._inputPipeValues[pipeId];
    }
    getInputs() {
        return this._inputPipeValues;
    }
    getOutput(pipeId) {
        console.assert(!!pipeId);
        return this._outputPipeValues[pipeId];
    }
    setOutput(pipeId, updateBlob) {
        console.assert(!!pipeId);
        var outputs = {};
        outputs[pipeId] = updateBlob;
        this.setOutputs(outputs);
    }
    setOutputs(outputs) {
        if (!merge(this._outputPipeValues, outputs)) {
            return;
        }
        this.sendRpc(JsonRpcMethodsFromChild.OutputsUpdate, outputs);
    }
    getOutputs() {
        return this._outputPipeValues;
    }
    disableNotifyOnHashUrlChange() {
        window.removeEventListener("hashchange", this._onHashUrlChange);
    }
    _onHashUrlChange(_) {
        const payload = {
            hash: window.location.hash,
            metaframe: this.id,
        };
        this.sendRpc(JsonRpcMethodsFromChild.HashParamsUpdate, payload);
    }
    sendRpc(method, params) {
        if (this._isIframe) {
            const message = {
                jsonrpc: "2.0",
                id: ++this._messageSendCount,
                method: method,
                params: params,
                iframeId: this.id,
                parentId: this._parentId,
            };
            window.parent.postMessage(message, "*");
        }
        else {
            this.log("Cannot send JSON-RPC window message: there is no window.parent which means we are not an iframe");
        }
    }
    onMessage(e) {
        if (typeof e.data === "object") {
            let jsonrpc = e.data;
            if (jsonrpc.jsonrpc === "2.0") {
                var method = jsonrpc.method;
                if (!(method == JsonRpcMethodsFromParent.SetupIframeServerResponse ||
                    (jsonrpc.parentId == this._parentId && jsonrpc.iframeId == this.id))) {
                    this.error(`window.message: received message but jsonrpc.parentId=${jsonrpc.parentId} _parentId=${this._parentId} jsonrpc.iframeId=${jsonrpc.iframeId} id=${this.id}`);
                    return;
                }
                switch (method) {
                    case JsonRpcMethodsFromParent.SetupIframeServerResponse:
                        this._resolveSetupIframeServerResponse(jsonrpc.params);
                        break;
                    case JsonRpcMethodsFromParent.InputsUpdate:
                        if (this._state !== MetaframeLoadingState.Ready) {
                            throw "Got InputsUpdate but metaframe is not MetaframeLoadingState.Ready";
                        }
                        this.setInternalInputsAndNotify(jsonrpc.params.inputs);
                        break;
                    case JsonRpcMethodsFromParent.MessageAck:
                        if (this.debug)
                            this.log(`ACK: ${JSON.stringify(jsonrpc)}`);
                        break;
                    default:
                        if (this.debug)
                            this.log(`window.message: unknown JSON-RPC method: ${JSON.stringify(jsonrpc)}`);
                        break;
                }
                this.emit(MetaframeEvents.Message, jsonrpc);
            }
        }
    }
}
Metaframe.version = VERSION_METAFRAME;
Metaframe.ERROR = MetaframeEvents.Error;
Metaframe.CONNECTED = MetaframeEvents.Connected;
Metaframe.INPUT = MetaframeEvents.Input;
Metaframe.INPUTS = MetaframeEvents.Inputs;
Metaframe.MESSAGE = MetaframeEvents.Message;
export class MetaframePlugin {
    constructor(metaframe) {
        this._metaframe = metaframe;
        this.requestState = this.requestState.bind(this);
        this.onState = this.onState.bind(this);
        this.getState = this.getState.bind(this);
        this.setState = this.setState.bind(this);
        this.onDefinition = this.onDefinition.bind(this);
        this.getDefinition = this.getDefinition.bind(this);
        this.setDefinition = this.setDefinition.bind(this);
    }
    requestState() {
        var payload = {
            method: ApiPayloadPluginRequestMethod.State,
        };
        this._metaframe.sendRpc(JsonRpcMethodsFromChild.PluginRequest, payload);
    }
    onState(listener) {
        const disposer = this._metaframe.onInput(METAPAGE_KEY_STATE, listener);
        if (this.getState()) {
            listener(this.getState());
        }
        return disposer;
    }
    getState() {
        return this._metaframe.getInput(METAPAGE_KEY_STATE);
    }
    setState(state) {
        this._metaframe.setOutput(METAPAGE_KEY_STATE, state);
    }
    onDefinition(listener) {
        var disposer = this._metaframe.onInput(METAPAGE_KEY_DEFINITION, listener);
        if (this.getDefinition()) {
            listener(this.getDefinition());
        }
        return disposer;
    }
    setDefinition(definition) {
        this._metaframe.setOutput(METAPAGE_KEY_DEFINITION, definition);
    }
    getDefinition() {
        return this._metaframe.getInput(METAPAGE_KEY_DEFINITION);
    }
}
//# sourceMappingURL=Metaframe.js.map